<template>
  <div class="daily-question-container">
    <!-- 顶部导航栏 -->
    <div class="daily-header">
      <div class="header-left">
        <i class="el-icon-arrow-left back-btn" @click="goBack"></i>
        <h1 class="page-title">{{ t('quickPractice') }}</h1>
      </div>
      <div class="header-right">
        <div class="timer-display" :style="{ marginRight: '20px', fontWeight: 'bold', color: timerColor }">
          <i class="el-icon-time"></i> {{ currentDurationDisplay }}
        </div>
        <el-radio-group v-model="langMode" size="small" @change="onLangChange" style="margin-right:10px;">
          <el-radio-button label="zh">中文</el-radio-button>
          <el-radio-button label="en">English</el-radio-button>
          <el-radio-button label="bi">双语</el-radio-button>
        </el-radio-group>
        <el-button
          type="primary"
          size="small"
          icon="el-icon-refresh"
          @click="refreshQuestions"
          :loading="loading">
          {{ t('refresh') }}
        </el-button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-loading :loading="loading" :text="t('loadingQuestions')" background="rgba(0, 0, 0, 0.8)" />
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="error-content">
        <i class="el-icon-warning error-icon"></i>
        <p class="error-message">{{ error }}</p>
        <el-button type="primary" @click="refreshQuestion">重新加载</el-button>
      </div>
    </div>

    <!-- 内容区 -->
    <div v-else-if="currentQuestion" class="daily-content">
      <div v-if="plannedIds && plannedIds.length" class="plan-card-grid">
        <div
          v-for="(q, idx) in questions"
          :key="q.id || idx"
          class="plan-card"
          :class="{ active: idx === currentIndex }"
          @click="selectQuestion(idx)"
        >
          <div class="plan-card-title">题目 {{ idx + 1 }}</div>
          <div class="plan-card-id">ID: {{ q.id }}</div>
        </div>
      </div>
      <!-- 题目卡片 -->
      <div class="question-card">
        <div class="question-header">
          <div class="question-title">
            <span class="question-number">{{ questionTitle }}</span>
            <span class="question-date">{{ todayDate }}</span>
          </div>
          <div class="question-meta">
            <el-button
              type="text"
              icon="el-icon-warning"
              @click="openFeedbackDialog"
              style="color: #E6A23C;"
            >
              问题反馈
            </el-button>
          </div>
        </div>

        <div class="question-content" v-mathjax v-html="renderWithMath(getQuestionContent(currentQuestion, langMode))"></div>

        <!-- 题目图片显示 -->
        <div v-if="getQuestionImages(currentQuestion).length > 0" class="question-images">
          <img
            v-for="(imgUrl, idx) in getQuestionImages(currentQuestion)"
            :key="idx"
            :src="imgUrl"
            class="question-image"
            @error="handleImageError"
          />
        </div>

        <!-- 选项显示 -->
        <div class="question-options" v-if="getOptionsSource(currentQuestion)">
          <el-radio-group v-model="userAnswer" @change="onAnswerChange">
            <el-radio
              v-for="(option, idx) in parseOptions(getOptionsSource(currentQuestion))"
              :key="option.key"
              :label="option.key"
              class="option-item"
            >
              <!-- 如果选项有图片，显示图片 -->
              <div v-if="hasOptionImages(currentQuestion) && getOptionImage(currentQuestion, idx)" class="option-with-image">
                <span class="option-key">{{ option.key }}.</span>
                <img :src="getOptionImage(currentQuestion, idx)" class="option-image" @error="handleImageError" />
              </div>
              <!-- 否则显示文字 -->
              <span v-else v-mathjax v-html="renderWithMath(option.key + '. ' + option.value)"></span>
            </el-radio>
          </el-radio-group>
        </div>

        <!-- 如果没有选项，显示答案输入框 -->
        <div class="question-answer-input" v-else>
          <el-input
            v-model="userAnswer"
            @input="onAnswerChange"
            :placeholder="t('answerPlaceholder')"
            maxlength="10"
            show-word-limit
            style="width: 200px"
          ></el-input>
          <span class="answer-tip">（请输入答案，如：A、B、C、D、E 或具体数值）</span>
        </div>

        <!-- 图片上传 -->
        <div class="upload-section">
          <div class="upload-title">{{ t('uploadImageOptional') }}</div>
          <el-upload
            :action="uploadAction"
            :accept="'image/*'"
            :before-upload="handleBeforeUpload"
            :on-change="handleFileChange"
            :on-remove="handleRemoveImage"
            :on-exceed="handleExceed"
            :multiple="false"
            :limit="1"
            :headers="uploadHeaders"
            :file-list="imageFileList"
            list-type="picture"
            :auto-upload="false"
          >
            <el-button size="small" type="primary">{{ t('clickUpload') }}</el-button>
            <div slot="tip" class="el-upload__tip">{{ t('uploadTip') }}</div>
          </el-upload>
        </div>

        <!-- 答题状态 -->
        <div v-if="answerResults && answerResults[currentIndex] !== null" class="answer-status">
          <div v-if="answerResults[currentIndex] === true" class="correct-answer">
            <i class="el-icon-check"></i>
            <span>回答正确！</span>
          </div>
          <div v-else class="incorrect-answer">
            <i class="el-icon-close"></i>
            <span>回答错误，正确答案是：{{ currentQuestion.answer }}</span>
          </div>
        </div>

        <!-- 显示解析（如果已答题） -->
        <div v-if="answerResults && answerResults[currentIndex] !== null && currentQuestion.analysis" class="analysis-section">
          <div class="analysis-title">{{ t('analysis') }}：</div>
          <div class="analysis-content" v-mathjax v-html="renderMathFormula(getAnalysisContent(currentQuestion, langMode))"></div>
        </div>

        <!-- 操作按钮 -->
        <div class="action-buttons" style="margin-top: 8px;">
          <el-button type="default" size="large" @click="prevQuestion" :disabled="currentIndex===0">{{ t('prev') }}</el-button>
          <!-- <el-button
            type="primary"
            size="large"
            @click="submitAllAnswers"
            :disabled="!isAllAnswered()"
            :loading="submitting">
            提交全部
          </el-button> -->
          <el-button
            type="primary"
            plain
            size="large"
            @click="submitCurrentAnswer"
            :disabled="isCurrentFinished || !userAnswer || String(userAnswer).trim()===''"
            :loading="singleSubmitting">
            提交本题
          </el-button>
          <el-button type="default" size="large" @click="nextQuestion" :disabled="currentIndex>=questions.length-1 && !endlessMode" :loading="endlessLoading">{{ t('next') }}</el-button>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="empty-container">
      <div class="empty-content">
        <i class="el-icon-document empty-icon"></i>
        <p class="empty-message">{{ t('noQuestions') }}</p>
        <el-button type="primary" @click="refreshQuestions">{{ t('reload') }}</el-button>
      </div>
    </div>

  <!-- AI判题结果弹窗 -->
  <el-dialog
    title="AI判题结果"
    :visible.sync="showAiAnalysisDialog"
    width="600px"
    :before-close="() => { showAiAnalysisDialog = false }"
    :append-to-body="true"
  >
    <div class="ai-analysis-content" v-if="aiAnalysisResult">
      <div class="analysis-section">
        <h4 class="section-title">题目</h4>
        <div class="section-content">{{ aiAnalysisResult.question }}</div>
      </div>

      <div class="analysis-section">
        <h4 class="section-title">答案对比</h4>
        <div class="answer-comparison">
          <div class="answer-item">
            <span class="answer-label">你的答案：</span>
            <span class="answer-value user-answer">{{ aiAnalysisResult.user_answer }}</span>
          </div>
          <div class="answer-item">
            <span class="answer-label">正确答案：</span>
            <span class="answer-value correct-answer">{{ aiAnalysisResult.correct_answer }}</span>
          </div>
        </div>
      </div>

      <div class="analysis-section" v-if="aiAnalysisResult.ai_analysis">
        <h4 class="section-title">AI分析结果</h4>
        <div class="ai-result">
          <div class="result-item">
            <span class="result-label">判题结果：</span>
            <el-tag :type="aiAnalysisResult.ai_analysis.answer_correct ? 'success' : 'danger'" size="medium">
              {{ aiAnalysisResult.ai_analysis.answer_correct ? '正确' : '错误' }}
            </el-tag>
          </div>
          <div class="result-item" v-if="aiAnalysisResult.ai_analysis.step_analysis">
            <span class="result-label">步骤分析：</span>
            <div class="result-content">{{ aiAnalysisResult.ai_analysis.step_analysis }}</div>
          </div>
          <div class="result-item" v-if="aiAnalysisResult.ai_analysis.suggestion">
            <span class="result-label">改进建议：</span>
            <div class="result-content suggestion">{{ aiAnalysisResult.ai_analysis.suggestion }}</div>
          </div>
        </div>
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="showAiAnalysisDialog = false">关闭</el-button>
      <el-button type="primary" @click="showAiAnalysisDialog = false">确定</el-button>
    </span>
  </el-dialog>

  <!-- 统一提交结果弹窗 -->
  <el-dialog
    title="提交结果"
    :visible.sync="showResultDialog"
    width="600px"
    :append-to-body="true"
  >
    <div class="result-summary">
      <p>本次共 {{ questions.length }} 题，答对 {{ correctCount }} 题，得分 {{ finalScore }} 分</p>
      <div v-if="wrongQuestions && wrongQuestions.length">
        <div style="font-weight:600; margin:10px 0;">错题列表</div>
        <ul>
          <li v-for="(w, i) in wrongQuestions" :key="w.id || i">题目ID：{{ w.id }}</li>
        </ul>
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button type="primary" @click="showResultDialog=false">确定</el-button>
    </span>
  </el-dialog>

  <!-- 问题反馈弹窗 -->
  <QuestionFeedbackDialog
    v-if="showFeedbackDialog && currentQuestion && currentQuestion.id"
    :visible.sync="showFeedbackDialog"
    :question-id="Number(currentQuestion.id)"
    :course-id="null"
    @success="handleFeedbackSuccess"
  />

  <!-- 本题错误操作选择弹窗 -->
  <el-dialog
    title="答错了，下一步怎么做？"
    :visible.sync="showWrongActionDialog"
    width="420px"
    :append-to-body="true"
  >
    <div>
      <p style="margin-bottom: 12px;">你可以选择：</p>
      <ul style="line-height: 1.8; padding-left: 18px;">
        <li>立即巩固：查看解析与建议，巩固本题相关知识点</li>
        <li>稍后复习：将本题加入复习列表，之后再回顾</li>
      </ul>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="onReviewLater">稍后复习</el-button>
      <el-button type="primary" @click="onConsolidateNow">立即巩固</el-button>
    </span>
  </el-dialog>

  <!-- 立即巩固弹窗：展示解析、允许重做、并在答对后练习相似题 -->
  <el-dialog
    title="立即巩固"
    :visible.sync="showConsolidateDialog"
    width="680px"
    :append-to-body="true"
  >
    <div v-if="currentQuestion">
      <!-- 解析展示 -->
      <div class="analysis-section">
        <div class="analysis-title">本题解析</div>
        <div class="analysis-content" v-mathjax v-html="renderMathFormula(getAnalysisContent(currentQuestion, langMode))"></div>
      </div>

      <!-- 重做当前题 -->
      <div class="redo-section" style="margin-top: 12px;">
        <div style="font-weight:600; margin-bottom:6px;">立即重做</div>
        <div v-if="getOptionsSource(currentQuestion)">
          <el-radio-group v-model="redoAnswer">
            <el-radio v-for="opt in parseOptions(getOptionsSource(currentQuestion))" :key="opt.key" :label="opt.key">
              <span v-mathjax v-html="renderWithMath(opt.key + '. ' + opt.value)"></span>
            </el-radio>
          </el-radio-group>
        </div>
        <div v-else>
          <el-input v-model="redoAnswer" placeholder="请输入答案" style="width: 200px"></el-input>
        </div>
        <div v-if="redoResult!==null" class="answer-status" style="margin-top:8px;">
          <el-tag :type="redoResult ? 'success' : 'danger'">{{ redoResult ? '回答正确' : '回答错误' }}</el-tag>
        </div>
        <div style="margin-top: 8px;">
          <el-button type="primary" @click="submitRedoInDialog" :disabled="!redoAnswer || String(redoAnswer).trim()===''" :loading="redoSubmitting">提交重做</el-button>
        </div>
      </div>

      <!-- 相似题练习（仅当重做正确后显示） -->
      <div v-if="showSimilarSection" class="similar-section" style="margin-top: 16px;">
        <div style="display:flex;align-items:center;justify-content:space-between;">
          <div style="font-weight:600;">相似题练习</div>
          <el-tag v-if="loadingSimilar" type="info">加载中...</el-tag>
        </div>
        <div v-if="similarList && similarList.length">
          <div style="margin: 8px 0; color:#909399;">第 {{ similarIndex + 1 }} / {{ similarList.length }} 题</div>
          <div class="question-content" v-mathjax v-html="renderWithMath(getQuestionContent(similarCurrent, langMode))"></div>
          <div v-if="getQuestionImages(similarCurrent).length > 0" class="question-images">
            <img
              v-for="(imgUrl, idx) in getQuestionImages(similarCurrent)"
              :key="idx"
              :src="imgUrl"
              class="question-image"
              @error="handleImageError"
            />
          </div>
          <div class="question-options" v-if="getOptionsSource(similarCurrent)">
            <el-radio-group v-model="similarAnswer">
              <el-radio v-for="opt in parseOptions(getOptionsSource(similarCurrent))" :key="opt.key" :label="opt.key">
                <span v-mathjax v-html="renderWithMath(opt.key + '. ' + opt.value)"></span>
              </el-radio>
            </el-radio-group>
          </div>
          <div v-else style="margin-top:6px;">
            <el-input v-model="similarAnswer" placeholder="请输入答案" style="width: 200px"></el-input>
          </div>
          <div v-if="similarResult!==null" class="answer-status" style="margin-top:8px;">
            <el-tag :type="similarResult ? 'success' : 'danger'">{{ similarResult ? '回答正确' : '回答错误' }}</el-tag>
          </div>
          <div v-if="similarResult===false && similarCurrent" class="analysis-section" style="margin-top:12px;padding:12px;background:#fef0f0;border-left:4px solid #f56c6c;border-radius:4px;">
             <div style="font-weight:600;margin-bottom:6px;color:#f56c6c;">错题解析</div>
             <div class="analysis-content" v-mathjax v-html="renderMathFormula(getAnalysisContent(similarCurrent, langMode))"></div>
             <div style="margin-top:8px;font-size:13px;color:#909399;">此题已自动加入错题本</div>
          </div>
          <div style="margin-top: 8px; display:flex; gap:8px;">
            <el-button type="primary" @click="submitSimilarInDialog" :disabled="similarResult !== null || !similarAnswer || String(similarAnswer).trim()===''">提交此题</el-button>
            <el-button @click="nextSimilar" :disabled="similarResult === null">{{ similarIndex >= similarList.length - 1 ? '完成练习' : '下一题' }}</el-button>
          </div>
        </div>
        <div v-else-if="!loadingSimilar" style="color:#909399;">未找到相似题</div>
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="closeConsolidateDialog">关闭</el-button>
    </span>
  </el-dialog>
  </div>
</template>

<script>
import { getAllByRepoId as getCourseQuestionsByRepoId, getQuestionById as getCourseQuestionById, getSimilarQuestions } from '@/api/courseQuestion'
import { recommendEndlessQuestions as recommendEndlessQuestionsGeneral } from '@/api/question'
import { uploadToBucket } from '@/api/file'
import { judgeSolution } from '@/api/ai'
import { addRecord, getRecordPage } from '@/api/userQuestionRecord'
import { listAllGeneralKnowledge } from '@/api/knowledge'
import { getProgressByDate, upsertTodayPlan, updateProgressFinished, getProgressPaging, getSchemeByUserIdAndRepoId, updateSchemeFinishedIds as updateSchemeFinished } from '@/api/studyPlan'
import { userStatusAdd } from '@/api/userStatus'
import { getTokenInfo } from '@/utils/jwtUtils'
import QuestionFeedbackDialog from '@/components/QuestionFeedbackDialog'
import katex from 'katex'
import 'katex/dist/katex.min.css'

export default {
  name: 'QuickPractice',
  components: {
    QuestionFeedbackDialog
  },
  directives: {
    mathjax: {
      inserted(el) {
        setTimeout(() => {
          try {
            if (window && window.MathJax && typeof window.MathJax.typesetPromise === 'function') {
              if (typeof window.MathJax.typesetClear === 'function') window.MathJax.typesetClear([el])
              window.MathJax.typesetPromise([el]).catch(() => {})
            }
          } catch (_) {}
        }, 50)
      },
      componentUpdated(el) {
        setTimeout(() => {
          try {
            if (window && window.MathJax && typeof window.MathJax.typesetPromise === 'function') {
              if (typeof window.MathJax.typesetClear === 'function') window.MathJax.typesetClear([el])
              window.MathJax.typesetPromise([el]).catch(() => {})
            }
          } catch (_) {}
        }, 50)
      }
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      repoId: null,
      questionId: 407,
      difficultyLevel: '',
      langMode: 'bi',
      questions: [],
      currentIndex: 0,
      currentQuestion: null,
      userAnswer: '',
      hasAnswered: false,
      userAnswers: [],
      answerResults: [],
      submitting: false,
      todayDate: '',
      uploadAction: 'api/questions/uploadImage',
      uploadHeaders: {},
      imageFileList: [],
      uploadedImageFile: null,
      uploadImageUrl: '',

      // AI 判题弹窗
      showAiAnalysisDialog: false,
      aiAnalysisResult: null,

      // 日期
      todayDate: '',
      plannedIds: [],

      // 统一提交结果
      showResultDialog: false,
      wrongQuestions: [],
      finalScore: 0,
      correctCount: 0,
      // 单题提交 loading 状态
      singleSubmitting: false,
      // 每日学习统计
      statPlanTotal: 0,
      statCompleted: 0,
      statCorrect: 0,
      statWrong: 0,
      statReviewed: 0,
      statSchemeId: null,
      statProgressId: null,
      // 已完成题目的 ID 列表（用于上报 finished_id）
      statFinishedIds: [],
      // 答错后的操作弹窗
      showWrongActionDialog: false,
      // 立即巩固弹窗及状态
      showConsolidateDialog: false,
      redoAnswer: '',
      redoResult: null,
      redoSubmitting: false,
      showSimilarSection: false,
      loadingSimilar: false,
      similarList: [],
      similarIndex: 0,
      similarAnswer: '',
      similarResult: null,
      similarCorrectCount: 0,
      similarCorrectMap: [],
      similarReviewMarked: false,
      similarSubmittedMap: {},
      // 知识点名称映射缓存
      _knowledgeIdNameMap: {}, // id -> name
      _knowledgeIdToParentId: {}, // id -> parentId
      _knowledgeParentNameMap: {}, // childId -> parentName

      // 通用题目缓存（用于相似题/减少重复拉取）
      _repoQuestionsCache: null,
      // 计时器相关
      questionStartTime: null, // 当前题目开始时间
      questionElapsedMs: null,
      questionDurations: [], // 每道题目的解题用时（毫秒）
      timerInterval: null,
      currentDurationDisplay: '00:00',
      timerColor: '#67C23A',
      // 问题反馈
      showFeedbackDialog: false,
      // 无尽模式
      endlessMode: false,
      endlessLoading: false,
      endlessLoadedIds: new Set(), // 已加载过的题目ID，避免重复

      // status 汇总上报：今日任务完成时 1 条；无尽刷题退出时 1 条
      plannedIdSet: new Set(),
      plannedAnswerMap: {}, // { [questionId]: boolean(isCorrect) }
      dailyStatusReported: false,
      endlessAnswerMap: {}, // { [questionId]: boolean(isCorrect) }
      endlessStatusReported: false,
      endlessPhaseStarted: false
    }
  },
  created() {
    this.initUploadHeaders()
    // 先启动知识点映射加载，完成后尝试回填现有题目的名称
    this.initKnowledgeNameMap().then(() => {
      this.recomputeKnowledgeNames()
    }).catch(() => {})
    this.initPage()
  },
  methods: {
    async getRepoQuestionsCached() {
      const repoIdVal = Number(this.repoId) || this.repoId
      if (!repoIdVal) return []
      if (Array.isArray(this._repoQuestionsCache) && this._repoQuestionsCache.length) return this._repoQuestionsCache
      const resp = await getCourseQuestionsByRepoId(repoIdVal)
      const raw = resp
      const list = Array.isArray(raw)
        ? raw
        : (Array.isArray(raw?.data?.data) ? raw.data.data : (Array.isArray(raw?.data) ? raw.data : []))
      this._repoQuestionsCache = Array.isArray(list) ? list : []
      return this._repoQuestionsCache
    },
    getKnowledgeKey(question) {
      if (!question) return ''
      const v = question.knowledge || question.knowledgeId || question.knowledgeStr || question.knowledgeName || ''
      return String(v).trim()
    },
    openFeedbackDialog() {
      const qid = this.currentQuestion && this.currentQuestion.id != null ? Number(this.currentQuestion.id) : NaN
      if (!qid || Number.isNaN(qid)) {
        this.$message.error('题目未加载完成，无法反馈')
        return
      }
      this.showFeedbackDialog = true
    },
    // 反馈成功回调
    handleFeedbackSuccess() {
      this.$message.success('感谢您的反馈，我们会尽快处理！')
      this.showFeedbackDialog = false
    },
    // 将文本中的 LaTeX 渲染为 KaTeX，兼容 $...$/$$...$$ 以及 \(\)/\[\]
    renderWithMath(text) {
      if (!text) return ''
      const input = this.normalizeLatexInput(String(text)
        .replace(/\r\n/g, '\n')
        .replace(/\\\\\(/g, '\\(')
        .replace(/\\\\\)/g, '\\)')
        .replace(/\\\\\[/g, '\\[')
        .replace(/\\\\\]/g, '\\]'))

      try {
        let html = input

        // 兼容 AMC 题目中常见的 \( ... \) / \[ ... \] 定界符
        // 先处理块级 \[ ... \] -> $$...$$
        html = html.replace(/\\\[([\s\S]+?)\\\]/g, (_, expr) => this.renderKatex(expr, true))
        // 再处理行内 \( ... \) -> $...$
        html = html.replace(/\\\(([\s\S]+?)\\\)/g, (_, expr) => this.renderKatex(expr, false))

        // 先处理块级 $$...$$
        html = html.replace(/\$\$([\s\S]+?)\$\$/g, (_, expr) => this.renderKatex(expr, true))
        // 再处理行内 $...$
        html = html.replace(/\$([^$\n]+?)\$/g, (_, expr) => this.renderKatex(expr, false))

        // 换行
        return html.replace(/\n/g, '<br>')
      } catch (e) {
        return input.replace(/\n/g, '<br>')
      }
    },
    renderKatex(expr, displayMode) {
      try {
        let formula = this.normalizeLatexInput(String(expr || '').trim().replace(/\\\\/g, '\\'))
        // 如果有全局修复函数则调用
        if (window && typeof window.fixLatexNotation === 'function') {
          formula = window.fixLatexNotation(formula)
        }
        return katex.renderToString(formula, {
          throwOnError: false,
          displayMode: !!displayMode,
          strict: false,
          trust: true
        })
      } catch (_) {
        return displayMode ? `$$${expr}$$` : `$${expr}$`
      }
    },
    normalizeLatexInput(text) {
      if (!text) return ''
      return String(text)
        .replace(/(^|[^\\])frac\s*\{/g, '$1\\frac{')
        .replace(/(^|[^\\])sqrt\s*\{/g, '$1\\sqrt{')
        .replace(/(^|[^\\])cdot\b/g, '$1\\cdot')
        .replace(/(^|[^\\])times\b/g, '$1\\times')
    },
    // 打开巩固弹窗
    openConsolidateDialog() {
      this.showConsolidateDialog = true
      this.redoAnswer = ''
      this.redoResult = null
      this.redoSubmitting = false
      this.showSimilarSection = false
      this.similarList = []
      this.similarIndex = 0
      this.similarAnswer = ''
      this.similarResult = null
      this.similarCorrectCount = 0
      this.similarCorrectMap = []
      this.similarReviewMarked = false
      this.similarSubmittedMap = {}
    },
    closeConsolidateDialog() {
      this.showConsolidateDialog = false
    },
    // 提交巩固弹窗中的“重做本题”
    async submitRedoInDialog() {
      const ans = (this.redoAnswer || '').toString().trim()
      if (!ans) return
      const correct = this.checkAnswerCorrect(ans, this.currentQuestion)
      this.redoResult = !!correct
      if (correct) {
        this.loadSimilarQuestions()
      }
    },
    async loadSimilarQuestions() {
      try {
        this.loadingSimilar = true
        this.showSimilarSection = true
        this.similarList = []
        this.similarIndex = 0
        this.similarSubmittedMap = {}

        const cur = this.currentQuestion
        if (!cur || !cur.id) {
          this.similarList = []
          this.similarCorrectMap = []
          return
        }

        const quesId = Number(cur.id)
        const limit = 3

        // 获取当前用户ID，用于智能推荐
        const user = getTokenInfo && getTokenInfo()
        const userId = user && user.id

        // 调用相似题接口，传入userId启用智能推荐
        // 推荐策略：优先推荐没做过的知识点相关题目，其次是做错的，最后是其他题目
        const resp = await getSimilarQuestions(quesId, limit, userId)
        const arr = resp?.data || []

        // 归一化相似题字段，确保渲染不缺字段
        this.similarList = (Array.isArray(arr) ? arr : []).map(q => {
          const contentCn = q.contentCn || q.contentcn || ''
          const contentEn = q.contentEn || q.contenten || ''
          const optionsCn = q.optionsCn || q.optionscn || ''
          const optionsEn = q.optionsEn || q.optionsen || ''
          const optionsStr = q.optionsStr || q.options || optionsCn || optionsEn || ''
          const analysis = q.analysis || q.analysisCn || q.analysiscn || q.analysisEn || q.analysisen || q.judge || ''
          return { ...q, contentCn, contentEn, optionsCn, optionsEn, optionsStr, analysis }
        })

        // 初始化每题正确标记数组
        this.similarCorrectMap = new Array(this.similarList.length).fill(false)
      } catch (e) {
        console.error('加载相似题失败:', e)
        this.similarList = []
        this.similarCorrectMap = []
      } finally {
        this.loadingSimilar = false
      }
    },
    async submitSimilarInDialog() {
      if (!this.similarCurrent) return
      const ans = (this.similarAnswer || '').toString().trim()
      if (!ans) return
      const similarKey = Number(this.similarCurrent.id) || `idx-${this.similarIndex}`
      if (this.similarSubmittedMap[similarKey]) {
        this.$message.info('本题已提交，请点击下一题')
        return
      }
      const correct = this.checkAnswerCorrect(ans, this.similarCurrent)
      this.similarResult = !!correct

      // 提交答题记录（做错加入错题本，做对计入学习计划）
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        const repoIdVal = Number(this.repoId) || this.repoId
        if (userId) {
          const q = this.similarCurrent
          // 构造记录 payload
          const payload = {
            userId: userId,
            quesId: q.id,
            quescontent_cn: q.contentCn || q.content,
            quescontent_en: q.contentEn,
            userAnswer: ans,
            answer: q.answer,
            judge: this.getRecordJudge(q),
            judge_cn: q.analysisCn || q.analysiscn || '',
            judge_en: q.analysisEn || q.analysisen || '',
            mynote: '',
            isReview: 0, // 相似题作为新题目记录，不做区分（错题本逻辑会根据 isRight=0 自动归入）
            knowledge: q.knowledgeName || this.getKnowledgeDisplayWithParent(q && (q.knowledge || q.knowledgeStr || q.knowledgeName)),
            isRight: correct ? 1 : 0,
            repoId: repoIdVal,
            time: new Date().toISOString()
          }
          // 异步写入记录
          await addRecord(payload)

          // 相似题既然已写入做题记录，则无论对错都计入学习计划
          if (q.id) {
            try {
              await this.updateSchemeFinishedIds(userId, repoIdVal, [q.id])
            } catch (schemeErr) {
              console.error('更新学习计划失败:', schemeErr)
            }
          }

          // 更新 study_progress 进度
          try {
            const qid = Number(q.id)
            if (!isNaN(qid) && qid > 0 && !this.statFinishedIds.includes(qid)) {
              this.statFinishedIds.push(qid)
            }
            // 相似题已提交后，统一将结果计入当日进度
            // 仅在首次提交该相似题时累计，避免重复点击导致重复累加
            if (correct) this.statCorrect += 1
            else this.statWrong += 1
            this.statCompleted += 1

            const todayStr = this.getTodayDate()
            // 先获取当日进度 ID
            let progressId = this.statProgressId
            if (!progressId) {
               const idRes = await getProgressByDate(userId, repoIdVal, todayStr)
               const idData = idRes?.data?.data ?? idRes?.data ?? idRes
               progressId = (typeof idData === 'number' || typeof idData === 'string') ? Number(idData) : (idData && idData.id)
            }
            // 再次尝试获取或创建
            if (!progressId) {
               const planRes = await upsertTodayPlan({ userId, repoId: repoIdVal, date: todayStr })
               const planData = planRes?.data?.data ?? planRes?.data ?? planRes
               progressId = planData && (planData.id || planData.ID)
               this.statProgressId = progressId
            }

            if (progressId) {
              // 重新拉取最新的 finishedId 列表（避免并发覆盖）
              const page = await getProgressPaging({ pageNum: 1, pageSize: 1, userId, repoId: repoIdVal, dateFrom: todayStr, dateTo: todayStr })
              const pdata = page?.data?.data ?? page?.data ?? {}
              const records = Array.isArray(pdata.records) ? pdata.records : []
              const first = records[0]

              if (first) {
                 const currentFinished = String(first.finished_id || first.finishedId || '')
                 const finishedSet = new Set(currentFinished.split(/[，,;\s]+/).filter(x => x).map(Number))

                 // 相似题已提交则记为完成；对错分别累计统计
                 let correctCount = Number(first.correct_count ?? first.correctCount ?? 0)
                 let wrongCount = Number(first.wrong_count ?? first.wrongCount ?? 0)

                 finishedSet.add(Number(q.id))
                 if (correct) {
                   correctCount += 1
                 } else {
                   wrongCount += 1
                 }

                 const newFinishedStr = Array.from(finishedSet).filter(n => !isNaN(n) && n > 0).join(',')
                 await updateProgressFinished(progressId, newFinishedStr, correctCount, wrongCount)
              }
            }
            // 再通过统一上报方法兜底，确保 study_progress 的 finished_id 与统计被回写
            await this.reportDailyProgress()
          } catch (progressErr) {
            console.error('更新相似题进度失败:', progressErr)
          }
        }
      } catch (e) {
        console.error('提交相似题记录失败:', e)
      }

      if (correct) {
        // 标记当前相似题已正确
        if (Array.isArray(this.similarCorrectMap) && this.similarIndex < this.similarCorrectMap.length) {
          if (!this.similarCorrectMap[this.similarIndex]) {
            this.similarCorrectMap.splice(this.similarIndex, 1, true)
            this.similarCorrectCount += 1
          }
        }
      }
      this.$set(this.similarSubmittedMap, similarKey, true)
    },
    async nextSimilar() {
      if (this.similarResult === null) return

      // 如果是最后一题，点击后完成复习
      if (this.similarList && this.similarIndex >= this.similarList.length - 1) {
        if (!this.similarReviewMarked) {
          try { await this.persistAnswerRecord(true, 1) } catch (_) {}
          this.similarReviewMarked = true
          // 将复习完成的题目ID（原题）追加至学习方案 finishedId
          try {
            const userInfo = getTokenInfo()
            const userId = userInfo ? userInfo.id : null
            const repoIdVal = Number(this.repoId) || this.repoId
            if (userId && this.currentQuestion?.id) {
              await this.updateSchemeFinishedIds(userId, repoIdVal, [this.currentQuestion.id])
            }
          } catch (_) {}
          this.$message.success('相似题练习完成，原题记为复习完成')
          // 统计复习数
          this.statReviewed += 1
          try { await this.reportDailyProgress() } catch (__) {}
          // 关闭弹窗并自动切换到主列表下一题
          this.closeConsolidateDialog()
          this.nextQuestion()
        }
        return
      }

      // 否则切换到下一道相似题
      this.similarIndex += 1
      this.similarAnswer = ''
      this.similarResult = null
    },
    // 单题提交批改
    async submitCurrentAnswer() {
      if (!this.currentQuestion) return
      const ans = (this.userAnswer || '').toString().trim()
      if (!ans) {
        this.$message.warning('请先作答本题')
        return
      }

      this.stopQuestionTimer()
      this.singleSubmitting = true
      try {
        let correct = false
        // 若有上传图片，优先使用 AI 判题
        if (this.uploadedImageFile) {
          try {
            const aiCorrect = await this.judgeAnswerWithImage()
            correct = !!aiCorrect
          } catch (_) {
            correct = this.isAnswerCorrect()
          }
        } else {
          correct = this.isAnswerCorrect()
        }
        // 更新当前题的判题结果
        if (Array.isArray(this.answerResults)) {
          this.$set(this.answerResults, this.currentIndex, !!correct)
        }
        this.hasAnswered = true
        if (correct) {
          // 正确：写入记录 + 统计（不逐题上报 amc-status）
          await this.persistAnswerRecord(true, 0)
          try { await this.persistSingleStatus(this.currentQuestion.id, true) } catch (_) {}
          try {
            const userInfo = getTokenInfo(); const userId = userInfo ? userInfo.id : null
            const repoIdVal = Number(this.repoId) || this.repoId
            // 将当前题ID追加至学习方案 finishedId
            try { await this.updateSchemeFinishedIds(userId, repoIdVal, [this.currentQuestion.id]) } catch (_) {}
            // 记录本次会话统计（汇总上报）
            try { this.recordStatusAnswer(this.currentQuestion.id, true) } catch (_) {}
          } catch (_) {}
          // 更新统计并上报
          this.statCompleted += 1
          this.statCorrect += 1
          // 记录已完成题目 ID
          if (this.currentQuestion && this.currentQuestion.id != null) {
            const qid = Number(this.currentQuestion.id)
            if (!this.statFinishedIds.includes(qid)) {
              this.statFinishedIds.push(qid)
            }
          }
          try { await this.reportDailyProgress() } catch (__) {}
          this.$message.success('答对了！')
        } else {
          // 错误：立即写入记录，然后给用户弹出操作（不逐题上报 amc-status）
          try { await this.persistAnswerRecord(false, 0) } catch (_) {}
          try { await this.persistSingleStatus(this.currentQuestion.id, false) } catch (_) {}
          try {
            const userInfo = getTokenInfo(); const userId = userInfo ? userInfo.id : null
            const repoIdVal = Number(this.repoId) || this.repoId
            // 将当前题ID追加至学习方案 finishedId（即使答错也要记录）
            try { await this.updateSchemeFinishedIds(userId, repoIdVal, [this.currentQuestion.id]) } catch (_) {}
            // 记录本次会话统计（汇总上报）
            this.recordStatusAnswer(this.currentQuestion.id, false)
          } catch (_) {}
          this.showWrongActionDialog = true
          // 记为完成+错误（用户已提交一次）
          this.statCompleted += 1
          this.statWrong += 1
          // 记录已完成题目 ID
          if (this.currentQuestion && this.currentQuestion.id != null) {
            const qid = Number(this.currentQuestion.id)
            if (!this.statFinishedIds.includes(qid)) {
              this.statFinishedIds.push(qid)
            }
          }
          try { await this.reportDailyProgress() } catch (__) {}
        }
      } catch (e) {
        this.$message.error('提交失败，请稍后重试')
      } finally {
        this.singleSubmitting = false
      }
    },
    async onConsolidateNow() {
      // 立即巩固：仅关闭错题弹窗并打开巩固弹窗（单题提交时错误已经写入记录）
      this.showWrongActionDialog = false
      this.openConsolidateDialog()
    },
    onReviewLater() {
      // 稍后复习：仅关闭弹窗
      this.showWrongActionDialog = false
    },
    // 记录本次会话的作答统计（不逐题上报 amc-status）
    recordStatusAnswer(questionId, isCorrect) {
      const qid = Number(questionId)
      if (!qid || isNaN(qid)) return
      const correct = !!isCorrect

      // 属于今日任务题目且尚未进入无尽阶段：写入 plannedAnswerMap
      if (!this.endlessPhaseStarted && this.plannedIdSet && this.plannedIdSet.size && this.plannedIdSet.has(qid)) {
        this.$set(this.plannedAnswerMap, String(qid), correct)
        return
      }
      // 否则归为无尽刷题统计
      this.$set(this.endlessAnswerMap, String(qid), correct)
    },

    async persistSingleStatus(questionId, isCorrect) {
      try {
        const qid = Number(questionId)
        if (!qid || isNaN(qid)) return
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        const repoIdVal = Number(this.repoId) || this.repoId
        if (!userId || !repoIdVal) return

        const source = `智能练习(${this.getTodayDate()})`

        const payload = {
          userId: userId,
          quesList: String(qid),
          count: 1,
          rightCount: isCorrect ? 1 : 0,
          source,
          repoId: repoIdVal
        }
        await userStatusAdd(payload)
      } catch (_) {}
    },

    // 今日任务题目全部完成时，上报 1 条 amc-status（包含今日任务题目列表）
    async maybeReportDailyStatus() {
      return
    },

    // 退出页面时（无尽刷题阶段）上报 1 条 amc-status
    flushEndlessStatusOnExit() {
      return
    },

    selectQuestion(index) {
      if (index >= 0 && index < this.questions.length) {
        this.currentIndex = index
        this.currentQuestion = this.questions[this.currentIndex]
        this.userAnswer = this.userAnswers[this.currentIndex] || ''
        // 切题时根据当前题的判题结果恢复显示状态
        if (Array.isArray(this.answerResults)) {
          this.hasAnswered = this.answerResults[this.currentIndex] !== null
        }
        // 重新开始计时
        this.$nextTick(() => {
          if (!this.hasAnswered) this.startQuestionTimer()
          else this.stopQuestionTimer()
        })
      }
    },
    // 处理文件变化
    handleFileChange(file, fileList) {
      if (file.raw) {
        this.uploadedImageFile = file.raw
        this.imageFileList = fileList
      }
    },
    // 将答题记录写入后端（与 DailyQuestion.vue 一致）
    async persistAnswerRecord(isCorrect, isReview = 0) {
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        if (!userId) return

        const q = this.currentQuestion || {}
        const repoIdVal = Number(this.repoId) || this.repoId

        // 计算解题用时（秒字符串）
        const duration = this.getQuestionDurationSeconds()

        // 获取题目的中英文内容
        const quesContentCn = q.contentCn || q.contentcn || q.content || ''
        const quesContentEn = q.contentEn || q.contenten || ''

        // 调试信息
        console.log('提交答题记录 - 题目ID:', q.id)
        console.log('- duration (秒):', duration)
        console.log('- quesContentCn:', quesContentCn ? quesContentCn.substring(0, 50) + '...' : '(空)')
        console.log('- quesContentEn:', quesContentEn ? quesContentEn.substring(0, 50) + '...' : '(空)')

        // 如果有上传图片，先上传图片获取URL
        let imagesJson = null
        if (this.uploadedImageFile) {
          try {
            const uploadRes = await uploadToBucket(this.uploadedImageFile, 'question-records')
            const imageUrl = uploadRes?.data?.data ?? uploadRes?.data ?? null
            if (imageUrl) {
              // 存储为JSON数组格式
              imagesJson = JSON.stringify([imageUrl])
              console.log('- 上传图片成功:', imageUrl)
            }
          } catch (uploadErr) {
            console.warn('上传图片失败:', uploadErr)
          }
        }

        const payload = {
          userId: userId,
          quesId: q.id,
          quescontent_cn: quesContentCn,
          quescontent_en: quesContentEn,
          duration: duration,
          userAnswer: (this.userAnswer || '').toString(),
          answer: q.answer,
          judge: this.getRecordJudge(q),
          judge_cn: q.analysisCn || q.analysiscn || '',
          judge_en: q.analysisEn || q.analysisen || '',
          mynote: '',
          isReview: Number(isReview) || 0,
          knowledge: q.knowledgeName || this.getKnowledgeDisplayWithParent(q && (q.knowledge || q.knowledgeStr || q.knowledgeName)),
          isRight: isCorrect ? 1 : 0,
          repoId: repoIdVal,
          time: new Date().toISOString(),
          images: imagesJson,
          aiAnalysis: this.aiAnalysisResult ? JSON.stringify(this.aiAnalysisResult) : null
        }

        await addRecord(payload)
      } catch (e) {
      }
    },
    // 每日学习统计上报
    async reportDailyProgress() {
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        if (!userId) return
        const repoIdVal = Number(this.repoId) || this.repoId
        // 解析当日记录 id（通过通用接口获取或创建）
        let progressId = this.statProgressId
        if (!progressId) {
          try {
            // 先尝试获取当日进度
            const idRes = await getProgressByDate(userId, repoIdVal, this.getTodayDate())
            const idData = idRes?.data?.data ?? idRes?.data ?? idRes
            progressId = (typeof idData === 'number' || typeof idData === 'string') ? Number(idData) : (idData && idData.id)
            // fallback: 通过 upsertTodayPlan 获取或创建
            if (!progressId) {
              const planRes = await upsertTodayPlan({ userId, repoId: repoIdVal, date: this.getTodayDate() })
              const planData = planRes?.data?.data ?? planRes?.data ?? planRes
              progressId = planData && (planData.id || planData.ID)
            }
            this.statProgressId = progressId || null
          } catch (_) {}
        }
        if (!progressId) return
        const finishedIdsStr = Array.isArray(this.statFinishedIds) && this.statFinishedIds.length
          ? this.statFinishedIds.join(',')
          : ''
        await updateProgressFinished(
          progressId,
          finishedIdsStr,
          Number(this.statCorrect) || 0,
          Number(this.statWrong) || 0
        )
        // 通知AMCCourse.vue刷新今日任务统计
        try {
          const { EventBus } = require('@/utils/eventBus')
          EventBus.$emit('refresh-today-stats')
        } catch (_) {}
      } catch (_) {}
    },
    // 从当日用户做题记录中回填每道题的作答与对错结果
    async initTodayAnswersFromRecords() {
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        const repoIdVal = Number(this.repoId) || this.repoId
        if (!userId || !repoIdVal || !Array.isArray(this.questions) || !this.questions.length) return

        const todayStr = this.getTodayDate()
        const res = await getRecordPage({
          pageNum: 1,
          pageSize: 1000,
          userId,
          repoId: repoIdVal,
          dateFrom: todayStr,
          dateTo: todayStr
        })
        const pdata = res?.data?.data ?? res?.data ?? {}
        const records = Array.isArray(pdata.records) ? pdata.records : []
        if (!records.length) return

        const idToIndex = new Map()
        this.questions.forEach((q, idx) => {
          if (q && q.id != null) {
            idToIndex.set(Number(q.id), idx)
          }
        })

        const finishedSet = new Set(this.statFinishedIds || [])

        records.forEach(r => {
          const qid = r.quesId ?? r.quesID ?? r.ques_id ?? r.questionId ?? r.ques_id
          if (qid == null) return
          const numId = Number(qid)
          const idx = idToIndex.get(numId)
          if (idx == null) return

          const userAns = (r.userAnswer ?? r.user_answer ?? '').toString()
          const isRightRaw = r.isRight ?? r.is_right ?? r.correct
          const isRight = isRightRaw != null ? (Number(isRightRaw) === 1 || isRightRaw === true) : null

          if (Array.isArray(this.userAnswers) && idx < this.userAnswers.length) {
            this.$set(this.userAnswers, idx, userAns)
          }
          if (Array.isArray(this.answerResults) && idx < this.answerResults.length && isRight !== null) {
            this.$set(this.answerResults, idx, !!isRight)
          }
          finishedSet.add(numId)
        })

        this.statFinishedIds = Array.from(finishedSet)
        // 切换到当前题时刷新 userAnswer 与 hasAnswered
        if (this.currentQuestion && this.currentIndex >= 0 && Array.isArray(this.userAnswers)) {
          this.userAnswer = this.userAnswers[this.currentIndex] || ''
          if (Array.isArray(this.answerResults)) {
            this.hasAnswered = this.answerResults[this.currentIndex] !== null
          }
        }
      } catch (_) {}
    },
    // 国际化简易函数
    t(key) {
      const en = this.langMode === 'en'
      const dict = {
        quickPractice: en ? 'Quick Practice' : '今日任务',
        refresh: en ? 'Refresh' : '刷新题目',
        loadingQuestions: en ? 'Loading questions...' : '正在加载题目...',
        answerPlaceholder: en ? 'Enter your answer' : '请输入答案',
        uploadImageOptional: en ? 'Upload Image (optional)' : '上传图片（可选）',
        clickUpload: en ? 'Upload' : '点击上传',
        uploadTip: en ? 'Images only, up to 10MB' : '仅支持图片格式，大小请控制在10MB内',
        analysis: en ? 'Explanation' : '解析',
        submit: en ? 'Submit' : '提交答案',
        retry: en ? 'Retry' : '重新答题',
        prev: en ? 'Previous' : '上一题',
        next: en ? 'Next' : '下一题',
        noQuestions: en ? 'No questions available' : '暂无题目',
        reload: en ? 'Reload' : '重新加载'
      }
      return dict[key] || key
    },
    // 处理图片上传前的钩子
    handleBeforeUpload(file) {
      return false // 阻止自动上传
    },
    // 处理图片移除
    handleRemoveImage() {
      this.uploadedImageFile = null
      this.imageFileList = []
    },
    // 处理文件超出限制
    handleExceed() {
      this.$message.warning('每次只能上传 1 个文件')
    },
    initUploadHeaders() {
      const { getToken } = require('@/utils/auth')
      this.uploadHeaders = { Authorization: getToken() }
    },
    async initPage() {
      this.repoId = this.$route.query.repoId
      if (this.$route.query.questionId) {
        this.questionId = Number(this.$route.query.questionId)
      }
      const planStr = this.$route.query.plan
      const plannedTotalFromRoute = this.$route.query.plannedTotal
      if (planStr && typeof planStr === 'string') {
        this.plannedIds = planStr.split(',').map(s => Number(String(s).trim())).filter(n => !isNaN(n) && n > 0)
      } else {
        this.plannedIds = []
      }
      if (this.$route.query.level) {
        this.difficultyLevel = this.normalizeLevel(this.$route.query.level)
      } else {
        this.difficultyLevel = '简单'
      }
      if (!this.repoId) {
        this.$message.error('缺少题库ID参数')
        this.goBack()
        return
      }
      // 无尽模式：路由传入 endless=1 时开启
      this.endlessMode = this.$route.query.endless === '1'

      // 初始化 status 汇总上报相关字段
      try {
        this.plannedIdSet = new Set((Array.isArray(this.plannedIds) ? this.plannedIds : []).map(n => Number(n)).filter(n => !isNaN(n) && n > 0))
      } catch (_) {
        this.plannedIdSet = new Set()
      }
      this.plannedAnswerMap = {}
      this.endlessAnswerMap = {}
      this.dailyStatusReported = false
      this.endlessStatusReported = false
      this.endlessPhaseStarted = false

      // 初始化计划总数：优先使用路由传入的原始计划题目数，否则退回到当前计划ID数量
      if (plannedTotalFromRoute != null && plannedTotalFromRoute !== undefined && plannedTotalFromRoute !== '') {
        const n = Number(plannedTotalFromRoute)
        if (!isNaN(n) && n > 0) {
          this.statPlanTotal = n
        } else {
          this.statPlanTotal = Array.isArray(this.plannedIds) ? this.plannedIds.length : 0
        }
      } else {
        this.statPlanTotal = Array.isArray(this.plannedIds) ? this.plannedIds.length : 0
      }
      // 从当日进度记录中初始化已完成统计（避免跨会话丢失已做题信息）
      await this.initDailyStatFromProgress()
      await this.loadQuestions()
      await this.initTodayAnswersFromRecords()
      this.todayDate = this.getTodayDate()
    },
    // 从 study-progress 当日记录中恢复统计字段
    async initDailyStatFromProgress() {
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        const repoIdVal = Number(this.repoId) || this.repoId
        if (!userId || !repoIdVal) return
        const todayStr = this.getTodayDate()
        const page = await getProgressPaging({ pageNum: 1, pageSize: 1, userId, repoId: repoIdVal, dateFrom: todayStr, dateTo: todayStr })
        const pdata = page?.data?.data ?? page?.data ?? {}
        const records = Array.isArray(pdata.records) ? pdata.records : []
        const first = records && records.length ? records[0] : null
        if (!first) return

        const finishedStr = String(first.finished_id || first.finishedId || '').trim()
        this.statCompleted = Number(first.finished_count ?? first.finishedCount ?? this.statCompleted) || 0
        this.statCorrect = Number(first.correct_count ?? first.correctCount ?? this.statCorrect) || 0
        let wrong = first.wrong_count ?? first.wrongCount
        if (wrong == null) wrong = Math.max(0, this.statCompleted - this.statCorrect)
        this.statWrong = Number(wrong) || 0
        this.statReviewed = Number(first.review_done ?? first.reviewDone ?? first.review_count ?? this.statReviewed) || 0

        // 初始化已完成题目ID列表
        if (finishedStr) {
          const ids = finishedStr.split(/[，,;\s]+/)
            .map(s => Number(String(s).trim()))
            .filter(n => !isNaN(n) && n > 0)
          this.statFinishedIds = Array.from(new Set(ids))
        }
      } catch (_) {}
    },
    async loadQuestions() {
      this.loading = true
      this.error = null
      try {
        let arr = []
        if (this.plannedIds && this.plannedIds.length) {
          const results = await Promise.all(this.plannedIds.map(id => getCourseQuestionById(id).catch(() => null)))
          arr = results
            .map(r => (r && (r.data?.data ?? r.data ?? r)) || null)
            .filter(Boolean)
        } else {
          const resp = await getCourseQuestionsByRepoId(this.repoId)
          arr = Array.isArray(resp?.data?.data) ? resp.data.data : (Array.isArray(resp?.data) ? resp.data : [])
          // 写入 repo 全量缓存，供相似题/前端筛选
          this._repoQuestionsCache = Array.isArray(arr) ? arr : []
        }
        if (!arr.length) {
          this.error = this.error || '暂无题目'
          this.currentQuestion = null
        } else {
          // 规范化题目结构，补全 content/analysis/knowledgeName，同时显式保留中英文字段
          const normalized = arr.map(q => {
            const content = q.content || q.contentCn || q.contentcn || q.contentEn || q.contenten || q.quesContent || ''
            const contentCn = q.contentCn || q.contentcn || q.content || ''
            const contentEn = q.contentEn || q.contenten || ''
            const analysis = q.analysis || q.analysisCn || q.analysiscn || q.analysisEn || q.analysisen || q.judge || ''
            const analysisCn = q.analysisCn || q.analysiscn || ''
            const analysisEn = q.analysisEn || q.analysisen || ''
            const knowledgeName = this.getKnowledgeDisplayWithParent(q.knowledge || q.knowledgeId || q.knowledgeStr || q.knowledgeName)
            return { ...q, content, contentCn, contentEn, analysis, analysisCn, analysisEn, knowledgeName }
          })
          this.questions = normalized
          // 默认跳转到第一道“未完成”的题目，若全部已完成则仍为第1题
          let startIndex = 0
          let allFinished = false
          if (Array.isArray(this.questions) && this.questions.length && Array.isArray(this.statFinishedIds) && this.statFinishedIds.length) {
            const finishedSet = new Set(this.statFinishedIds.map(id => Number(id)))
            const idx = this.questions.findIndex(q => q && q.id != null && !finishedSet.has(Number(q.id)))
            if (idx >= 0) {
              startIndex = idx
            } else {
              // 所有题目都已完成，跳转到最后一题
              startIndex = this.questions.length - 1
              allFinished = true
            }
          }
          this.currentIndex = startIndex
          // 无尽模式下，若所有题目已完成，提示用户可以继续刷题
          if (this.endlessMode && allFinished) {
            this.$nextTick(() => {
              this.$message.info('今日计划已全部完成！点击「下一题」可进入无尽模式继续刷题')
            })
          }
          this.currentQuestion = this.questions[this.currentIndex]
          this.userAnswers = new Array(this.questions.length).fill('')
          this.answerResults = new Array(this.questions.length).fill(null)
          this.questionDurations = new Array(this.questions.length).fill(0)
          this.userAnswer = ''
          this.hasAnswered = false
          this.uploadImageUrl = ''
          this.uploadedImageFile = null
          this.imageFileList = []
          // 开始第一题计时
          this.startQuestionTimer()
        }
      } catch (e) {
        this.error = e?.message || '加载题目失败'
      } finally {
        this.loading = false
      }
    },
    refreshQuestions() {
      this.loadQuestions().then(() => {
        // 刷新题目后，重新从今日记录中恢复作答状态
        // 这样可以避免刷新导致"已提交"状态丢失，因为 loadQuestions 会重置 userAnswers 和 answerResults
        this.initTodayAnswersFromRecords()
      })
    },
    onDifficultyChange() {
      this.refreshQuestions()
    },
    onLangChange() {},
    normalizeLevel(val) {
      const v = String(val || '').trim().toLowerCase()
      if (v === 'easy' || v === '简单') return '简单'
      if (v === 'medium' || v === '中等') return '中等'
      if (v === 'hard' || v === '困难') return '困难'
      return '简单'
    },
    levelToEnglish(val) {
      const v = String(val || '').trim()
      if (v === '简单' || v.toLowerCase() === 'easy') return 'easy'
      if (v === '中等' || v.toLowerCase() === 'medium') return 'medium'
      if (v === '困难' || v.toLowerCase() === 'hard') return 'hard'
      return ''
    },
    // 语言与内容
    getQuestionContent(question, mode) {
      if (!question) return ''
      const cn = question.contentCn || question.contentcn || question.content || ''
      const en = question.contentEn || question.contenten || ''
      const m = mode || 'bi'
      if (m === 'zh') return cn || en
      if (m === 'en') return en || cn
      if (cn && en) return `${cn}<br/><br/>${en}`
      return cn || en
    },
    getAnalysisContent(question, mode) {
      if (!question) return ''
      const cn = question.analysisCn || question.analysiscn || ''
      const en = question.analysisEn || question.analysisen || ''
      const fallback = question.analysis || question.judge || ''
      const m = mode || 'bi'
      if (m === 'zh') return cn || fallback
      if (m === 'en') return en || fallback
      if (cn && en) return `${cn}<br/><br/>${en}`
      return cn || en || fallback
    },
    getOptionsSource(question) {
      if (!question) return ''
      const cn = question.optionsCn || question.optionscn || ''
      const en = question.optionsEn || question.optionsen || ''
      const m = this.langMode || 'bi'
      if (m === 'zh') return cn || en || question.optionsStr || question.options || ''
      if (m === 'en') return en || cn || question.optionsStr || question.options || ''
      return cn || en || question.optionsStr || question.options || ''
    },
    getQuestionImages(question) {
      if (!question) return []
      const imagePaths = question.imagePaths || question.image_paths || question.image || ''
      if (!imagePaths) return []

      const convertPath = (path) => {
        if (!path) return ''
        // 如果已经是完整URL，直接返回
        if (path.startsWith('http')) return path
        // 如果已经是/api/file开头的URL，直接返回
        if (path.startsWith('/api/file/')) return path

        // 确保路径以/开头
        const normalizedPath = path.startsWith('/') ? path : `/${path}`

        // 通过后端文件预览接口访问
        return `/api/file/preview?path=${encodeURIComponent(normalizedPath)}`
      }

      try {
        // 如果是JSON字符串数组，解析它
        if (typeof imagePaths === 'string' && imagePaths.startsWith('[')) {
          const paths = JSON.parse(imagePaths)
          if (Array.isArray(paths)) {
            return paths.map(p => convertPath(p))
          }
        }
        // 如果是单个字符串路径
        if (typeof imagePaths === 'string' && imagePaths) {
          return [convertPath(imagePaths)]
        }
        // 如果已经是数组
        if (Array.isArray(imagePaths)) {
          return imagePaths.map(p => convertPath(p))
        }
      } catch (e) {
        console.error('解析题目图片路径失败:', e)
      }
      return []
    },
    handleImageError(e) {
      console.error('图片加载失败:', e.target.src)
      e.target.style.display = 'none'
    },
    hasOptionImages(question) {
      if (!question) return false
      const optionImages = question.optionImages || question.option_images || ''
      if (!optionImages) return false

      // 检查选项文本是否包含"对应题目图示选项"
      const optionsText = question.optionsCn || question.optionscn || question.optionsEn || question.optionsen || ''
      return optionsText.includes('对应题目图示选项') || optionsText.includes('对应图示选项')
    },
    getOptionImage(question, index) {
      if (!question) return ''
      const optionImages = question.optionImages || question.option_images || ''
      if (!optionImages) return ''

      try {
        // optionImages 是分号分隔的路径字符串
        const paths = optionImages.split(';').map(p => p.trim()).filter(p => p)
        if (index >= 0 && index < paths.length) {
          const path = paths[index]
          if (!path) return ''

          // 如果已经是完整URL，直接返回
          if (path.startsWith('http')) return path
          // 如果已经是/api/file开头的URL，直接返回
          if (path.startsWith('/api/file/')) return path

          // 确保路径以/开头
          const normalizedPath = path.startsWith('/') ? path : `/${path}`

          // 通过后端文件预览接口访问
          return `/api/file/preview?path=${encodeURIComponent(normalizedPath)}`
        }
      } catch (e) {
        console.error('解析选项图片路径失败:', e)
      }
      return ''
    },
    // 导航
    prevQuestion() {
      if (this.currentIndex > 0) {
        this.currentIndex -= 1
        this.currentQuestion = this.questions[this.currentIndex]
        this.userAnswer = this.userAnswers[this.currentIndex] || ''
        // 根据该题的判题结果恢复 hasAnswered 状态
        if (Array.isArray(this.answerResults)) {
          this.hasAnswered = this.answerResults[this.currentIndex] !== null
        } else {
          this.hasAnswered = false
        }
        // 重新开始计时
        this.$nextTick(() => {
          if (!this.hasAnswered) this.startQuestionTimer()
          else this.stopQuestionTimer()
        })
      }
    },
    nextQuestion() {
      if (this.currentIndex < this.questions.length - 1) {
        this.currentIndex += 1
        this.currentQuestion = this.questions[this.currentIndex]
        this.userAnswer = this.userAnswers[this.currentIndex] || ''
        // 根据下一题的判题结果恢复 hasAnswered 状态
        if (Array.isArray(this.answerResults)) {
          this.hasAnswered = this.answerResults[this.currentIndex] !== null
        } else {
          this.hasAnswered = false
        }
        // 重新开始计时
        this.$nextTick(() => {
          if (!this.hasAnswered) this.startQuestionTimer()
          else this.stopQuestionTimer()
        })
      } else if (this.endlessMode && !this.endlessLoading) {
        // 无尽模式：当前题目列表已做完，自动加载更多题目
        this.loadMoreEndlessQuestions()
      }
    },
    // 无尽模式：加载更多题目（调用后端接口，优先未做题、其次错题，排除今日计划题目）
    async loadMoreEndlessQuestions() {
      if (this.endlessLoading) return
      this.endlessLoading = true
      this.endlessPhaseStarted = true
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        const repoIdVal = Number(this.repoId) || this.repoId

        if (!userId || !repoIdVal) {
          this.$message.error('无法获取用户或题库信息')
          this.endlessLoading = false
          return
        }

        // 构建需要排除的题目ID列表：今日计划 + 已加载 + 无尽模式已加载
        const excludeIds = [
          ...(Array.isArray(this.plannedIds) ? this.plannedIds.map(id => Number(id)) : []),
          ...this.questions.map(q => Number(q.id)),
          ...Array.from(this.endlessLoadedIds)
        ].filter(id => !isNaN(id) && id > 0)

        // 调用后端推题接口
        const resp = await recommendEndlessQuestionsGeneral({
          userId: userId,
          repoId: repoIdVal,
          excludeIds: excludeIds,
          count: 5
        })

        const data = resp?.data?.data ?? resp?.data ?? {}
        // 通用推题接口可能直接返回数组，也可能包装在 {questions: []}
        const questions = Array.isArray(data)
          ? data
          : (data.questions ?? data.amcQuestions ?? [])
        const newCount = data.newCount ?? 0
        const wrongCount = data.wrongCount ?? 0

        if (!questions.length) {
          this.$message.info('题库中暂无更多新题目')
          this.endlessLoading = false
          return
        }

        // 规范化题目结构
        const normalized = questions.map(q => {
          const content = q.content || q.contentCn || q.contentcn || q.contentEn || q.contenten || q.quesContent || ''
          const contentCn = q.contentCn || q.contentcn || q.content || ''
          const contentEn = q.contentEn || q.contenten || ''
          const analysis = q.analysis || q.analysisCn || q.analysiscn || q.analysisEn || q.analysisen || q.judge || ''
          const analysisCn = q.analysisCn || q.analysiscn || ''
          const analysisEn = q.analysisEn || q.analysisen || ''
          const knowledgeName = this.getKnowledgeDisplayWithParent(q.knowledge || q.knowledgeId || q.knowledgeStr || q.knowledgeName)
          // 记录已加载的题目ID
          this.endlessLoadedIds.add(q.id)
          return { ...q, content, contentCn, contentEn, analysis, analysisCn, analysisEn, knowledgeName }
        })

        // 追加到题目列表
        const oldLen = this.questions.length
        this.questions.push(...normalized)
        // 扩展答案和结果数组
        this.userAnswers.push(...new Array(normalized.length).fill(''))
        this.answerResults.push(...new Array(normalized.length).fill(null))
        this.questionDurations.push(...new Array(normalized.length).fill(0))

        // 切换到新加载的第一题
        this.currentIndex = oldLen
        this.currentQuestion = this.questions[this.currentIndex]
        this.userAnswer = ''
        this.hasAnswered = false
        this.startQuestionTimer()

        // 提示加载了多少道新题/错题
        let msg = `已加载 ${normalized.length} 道题目`
        if (newCount > 0 && wrongCount > 0) {
          msg = `已加载 ${newCount} 道新题 + ${wrongCount} 道错题复习`
        } else if (wrongCount > 0) {
          msg = `已加载 ${wrongCount} 道错题复习`
        }
        this.$message.success(msg)
      } catch (e) {
        console.error('加载更多题目失败', e)
        this.$message.error('加载更多题目失败')
      } finally {
        this.endlessLoading = false
      }
    },
    // 解析选项字符串
    parseOptions(optionsStr) {
      if (!optionsStr) return []
      const options = []
      const optionRegex = /\(([A-E])\)\s*([^;]+)/g
      let match
      while ((match = optionRegex.exec(optionsStr)) !== null) {
        options.push({ key: match[1], value: match[2].trim() })
      }
      return options
    },
    onAnswerChange() {
      if (Array.isArray(this.userAnswers) && this.currentIndex >= 0) {
        this.$set(this.userAnswers, this.currentIndex, this.userAnswer || '')
      }
    },
    normalizeAnswerForCompare(answer, question) {
      const raw = String(answer == null ? '' : answer).trim()
      if (!raw) return ''
      const upperRaw = raw.toUpperCase()
      const optionLetterMatch = upperRaw.match(/^\(?\s*([A-Z])\s*\)?(?:[\.．、:：\s].*)?$/)
      if (optionLetterMatch) {
        return optionLetterMatch[1]
      }
      const optionsSource = this.getOptionsSource(question) || question?.optionsStr || question?.options || question?.optionsCn || question?.optionsEn || ''
      const options = this.parseOptions(optionsSource)
      const matchedOption = options.find(opt => {
        const key = String(opt.key || '').trim().toUpperCase()
        const value = String(opt.value || '').trim().toUpperCase()
        return upperRaw === `${key} ${value}` || upperRaw === `${key}.${value}` || upperRaw === `${key}. ${value}` || upperRaw === value
      })
      if (matchedOption) {
        return String(matchedOption.key || '').trim().toUpperCase()
      }
      return upperRaw.replace(/\s+/g, ' ').trim()
    },
    checkAnswerCorrect(userAnswer, question) {
      if (!question) return false
      const normalizedUserAnswer = this.normalizeAnswerForCompare(userAnswer, question)
      const normalizedCorrectAnswer = this.normalizeAnswerForCompare(question.answer, question)
      return normalizedUserAnswer !== '' && normalizedUserAnswer === normalizedCorrectAnswer
    },
    isAnswerCorrect() {
      return this.checkAnswerCorrect(this.userAnswer, this.currentQuestion)
    },
    // 难度显示
    getDifficultyClass(difficulty) {
      const level = this.getDifficultyLevel(difficulty)
      return `difficulty-${level}`
    },
    getDifficultyLevel(difficulty) {
      if (difficulty <= 0.3) return 'easy'
      if (difficulty <= 0.7) return 'medium'
      return 'hard'
    },
    getDifficultyText(difficulty) {
      const level = this.getDifficultyLevel(difficulty)
      const textMap = { easy: '简单', medium: '中等', hard: '困难' }
      return textMap[level] || '未知'
    },
    formatQuestionContent(content) {
      if (!content) return '暂无题目内容'
      return content.replace(/\n/g, '<br>')
    },
    async submitAllAnswers() {
      if (!this.isAllAnswered()) {
        this.$message.warning('请先完成所有题目作答再提交')
        return
      }
      this.submitting = true
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        const repoIdVal = Number(this.repoId) || this.repoId
        // 确保知识点名称映射已就绪
        if (!this._knowledgeIdNameMap || Object.keys(this._knowledgeIdNameMap).length === 0) {
          try { await this.initKnowledgeNameMap() } catch (_) {}
        }
        const records = []
        let right = 0
        for (let i = 0; i < this.questions.length; i++) {
          const q = this.questions[i]
          const ans = this.userAnswers[i]
          const correct = this.checkAnswerCorrect(ans, q)
          this.$set(this.answerResults, i, correct)
          if (correct) right += 1
          const payload = {
            userId: userId,
            quesId: q.id,
            quesContent: this.getRecordQuesContent(q),
            userAnswer: ans,
            answer: q.answer,
            judge: this.getRecordJudge(q),
            judge_cn: q.analysisCn || q.analysiscn || '',
            judge_en: q.analysisEn || q.analysisen || '',
            mynote: '',
            isReview: 0,
            knowledge: q.knowledgeName || this.getKnowledgeDisplayWithParent(q && (q.knowledge || q.knowledgeStr || q.knowledgeName)),
            isRight: correct ? 1 : 0,
            repoId: repoIdVal,
            time: new Date().toISOString()
          }
          records.push(addRecord(payload).catch(() => null))
        }
        await Promise.allSettled(records)
        this.hasAnswered = true
        const score = Math.round((right / this.questions.length) * 100)
        this.correctCount = right
        this.finalScore = score
        // 统计错题
        const wrong = []
        for (let i = 0; i < this.questions.length; i++) {
          if (!this.answerResults[i]) wrong.push(this.questions[i])
        }
        this.wrongQuestions = wrong
        this.showResultDialog = true

        // 上报 AMC 状态汇总
        try {
          const quesListStr = this.questions.map(q => q.id).join(',')
          const payload = {
            userId: userId,
            quesList: quesListStr,
            count: this.questions.length,
            rightCount: right,
            source: `智能练习(${this.getTodayDate()})`,
            repoId: repoIdVal
          }
          await userStatusAdd(payload)
        } catch (_) {}

        // 写入学习方案的 finishedId（追加今日题目ID）
        try {
          const todayIds = this.questions.map(q => q.id)
          await this.updateSchemeFinishedIds(userId, repoIdVal, todayIds)
        } catch (_) {}

        // 更新当日学习进度 finishedCount（若存在当日进度记录）
        try {
          const todayStr = this.getTodayDate()
          const page = await getProgressPaging({ pageNum: 1, pageSize: 1, userId, repoId: repoIdVal, dateFrom: todayStr, dateTo: todayStr })
          const pdata = page?.data?.data ?? page?.data ?? {}
          const records = Array.isArray(pdata.records) ? pdata.records : []
          const first = records && records.length ? records[0] : null
          const progressId = first ? (first.id ?? first.progressId) : null
          if (progressId) {
            const finishedIdsStr = this.questions && this.questions.length
              ? this.questions.map(q => q.id).join(',')
              : ''
            await updateProgressFinished(
              progressId,
              finishedIdsStr,
              right,
              this.questions.length - right
            )
          }
        } catch (_) {}

        if (score === 100) this.$message.success(`恭喜！全部答对！得分：${score}分`)
        else if (score >= 80) this.$message.success(`很好！答对${right}题，得分：${score}分`)
        else if (score >= 60) this.$message.warning(`继续加油！答对${right}题，得分：${score}分`)
        else this.$message.info(`已提交。答对${right}题，得分：${score}分`)
      } catch (e) {
        this.$message.error('提交失败，请稍后重试')
      } finally {
        this.submitting = false
      }
    },
    isAllAnswered() {
      if (!this.questions || !this.questions.length) return false
      if (!Array.isArray(this.userAnswers)) return false
      return this.userAnswers.every(a => a !== null && String(a || '').trim() !== '')
    },
    getFullOptionContentFor(question, optionKey) {
      if (!optionKey || !question?.optionsStr) {
        return optionKey || ''
      }
      const options = this.parseOptions(question.optionsStr)
      const option = options.find(opt => opt.key === optionKey)
      return option ? `${option.key} ${option.value}` : optionKey
    },
    // 规范化题干，确保不为 undefined（否则 axios 会省略该字段）
    getRecordQuesContent(q) {
      if (!q) return ''
      const cn = q.contentCn || q.contentcn
      const en = q.contentEn || q.contenten
      const base = q.content || q.quesContent
      // 优先中文，其次英文，最后原始字段
      return (cn || base || en || '').toString()
    },
    // 规范化解析字段
    getRecordJudge(q) {
      if (!q) return ''
      return (
        q.analysis || q.analysisCn || q.analysiscn || q.analysisEn || q.analysisen ||
        q.judge || q.explain || q.explanation || q.reason || q.remark || ''
      ).toString()
    },
    // 规范化知识点为字符串
    normalizeKnowledge(k) {
      if (!k) return ''
      if (Array.isArray(k)) return k.map(x => (x && x.name) ? x.name : String(x)).join(',')
      return String(k)
    },
    // 预加载知识点名称映射（精简版）
    async initKnowledgeNameMap() {
      try {
        const cacheKey = 'amc_knowledge_id_name_map'
        const cacheKeyParent = 'amc_knowledge_parent_name_map'
        const cacheStr = window.localStorage.getItem(cacheKey)
        const cacheStrParent = window.localStorage.getItem(cacheKeyParent)
        if (cacheStr) {
          try {
            this._knowledgeIdNameMap = JSON.parse(cacheStr) || {}
            if (cacheStrParent) this._knowledgeParentNameMap = JSON.parse(cacheStrParent) || {}
          } catch (_) {}
        }
        const resp = await listAllGeneralKnowledge()
        const arr = (resp && resp.data) || resp || []
        const map = {}
        const idToParent = {}
        const parentName = {}
        if (Array.isArray(arr)) {
          arr.forEach(k => {
            const id = k.id || k.ID
            const name = k.content || k.name || ''
            const pid = k.parentId || k.parent_id || k.ParentId || 0
            if (id != null && name) map[id] = name
            if (id != null) idToParent[id] = pid
          })
          Object.keys(idToParent).forEach(cid => {
            const pid = idToParent[cid]
            if (pid && map[pid]) parentName[cid] = map[pid]
          })
        }
        this._knowledgeIdNameMap = map
        this._knowledgeIdToParentId = idToParent
        this._knowledgeParentNameMap = parentName
        try { window.localStorage.setItem(cacheKey, JSON.stringify(map)) } catch (_) {}
        try { window.localStorage.setItem(cacheKeyParent, JSON.stringify(parentName)) } catch (_) {}
      } catch (_) { /* ignore */ }
    },
    // 将 knowledge 字段（可能为数字/逗号分隔/名称）映射为“父级-子级”的名称串
    getKnowledgeDisplayWithParent(field) {
      if (!field) return ''
      const map = this._knowledgeIdNameMap || {}
      const pmap = this._knowledgeParentNameMap || {}
      const parts = []
      const addById = (id) => {
        const name = map[id]
        if (!name) return
        const parent = pmap[id]
        parts.push(parent ? `${parent}-${name}` : name)
      }
      if (typeof field === 'number') { addById(field) } else if (Array.isArray(field)) { field.forEach(x => addById(Number(x))) } else {
        const s = String(field)
        const nums = s.split(/[，,;\s]+/).map(v => Number(String(v).trim())).filter(n => !isNaN(n) && n > 0)
        if (nums.length) nums.forEach(n => addById(n))
        else parts.push(s) // 已是名称
      }
      return parts.filter(Boolean).join(',')
    },
    // 判断字符串是否为“仅由数字、逗号、空格组成”的ID列表
    isIdListString(s) {
      if (!s) return false
      return /^[\d\s,，;]+$/.test(String(s))
    },
    // 在知识映射加载后，回填 questions 中的 knowledgeName
    recomputeKnowledgeNames() {
      if (!Array.isArray(this.questions) || this.questions.length === 0) return
      this.questions = this.questions.map(q => ({
        ...q,
        knowledgeName: this.getKnowledgeDisplayWithParent(q.knowledge || q.knowledgeId || q.knowledgeStr || q.knowledgeName)
      }))
    },
    // AI 判题：与 DailyQuestion 逻辑一致
    async judgeAnswerWithImage() {
      try {
        const formData = new FormData()
        const q = this.currentQuestion
        formData.append('question', q.content || q.contentCn || q.contentcn || q.contentEn || q.contenten || '')
        formData.append('user_answer', this.getFullOptionContent(this.userAnswer))
        formData.append('correct_answer', this.getFullOptionContent(q.answer))
        formData.append('explanation', q.analysis || q.analysisCn || q.analysiscn || q.analysisEn || q.analysisen || q.judge || '')
        if (this.uploadedImageFile) {
          formData.append('image', this.uploadedImageFile, this.uploadedImageFile.name)
        } else {
          throw new Error('没有找到上传的图片文件')
        }

        let loadingService = null
        try {
          loadingService = this.$loading({ lock: true, text: 'AI分析中...', spinner: 'el-icon-loading', background: 'rgba(0, 0, 0, 0.35)' })
          const response = await judgeSolution(formData)
          this.handleJudgeQuestionResponse(response)
        } finally {
          if (loadingService) loadingService.close()
        }
        const isCorrectFromAi = this.aiAnalysisResult && this.aiAnalysisResult.ai_analysis
          ? !!this.aiAnalysisResult.ai_analysis.answer_correct
          : null
        if (isCorrectFromAi === null) return this.isAnswerCorrect()
        return isCorrectFromAi
      } catch (error) {
        this.$message.warning('图片判断失败，使用原有逻辑判断')
        return this.isAnswerCorrect()
      }
    },
    getFullOptionContent(optionKey) {
      if (!optionKey || !this.currentQuestion?.optionsStr) {
        return optionKey || ''
      }
      const options = this.parseOptions(this.currentQuestion.optionsStr)
      const option = options.find(opt => opt.key === optionKey)
      return option ? `${option.key} ${option.value}` : optionKey
    },
    // 解析判题接口响应（与 DailyQuestion 对齐的宽松解析器）
    handleJudgeQuestionResponse(apiResponse) {
      try {
        // 兼容多种返回结构：data、result、data.result
        const raw = apiResponse?.data ?? apiResponse
        const data = raw?.result ?? raw?.data ?? raw
        const normalized = {
          question: data?.question || this.currentQuestion?.content || '',
          user_answer: data?.user_answer || this.getFullOptionContent(this.userAnswer),
          correct_answer: data?.correct_answer || this.getFullOptionContent(this.currentQuestion?.answer),
          ai_analysis: {
            answer_correct: !!(data?.ai_analysis?.answer_correct ?? data?.answer_correct ?? data?.correct),
            step_analysis: data?.ai_analysis?.step_analysis ?? data?.step_analysis ?? data?.analysis ?? '',
            suggestion: data?.ai_analysis?.suggestion ?? data?.suggestion ?? ''
          },
          status: data?.status || 'ok'
        }
        this.aiAnalysisResult = normalized
        this.showAiAnalysisDialog = true
      } catch (e) {
        // 解析失败不阻断主流程
      }
    },
    resetAnswer() {
      this.userAnswer = ''
      this.hasAnswered = false
    },
    getTodayDate() {
      const today = new Date()
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    },
    goBack() {
      this.$router.go(-1)
    },
    renderMathFormula(text) {
      return this.renderWithMath(text)
    },
    // 开始当前题目的计时
    startQuestionTimer() {
      this.stopQuestionTimer()
      this.questionStartTime = Date.now()
      this.questionElapsedMs = null
      this.currentDurationDisplay = '00:00'
      this.timerColor = '#67C23A'
      this.timerInterval = setInterval(() => {
        const start = this.questionStartTime
        if (!start) return
        const diff = Date.now() - start
        const totalSeconds = Math.floor(diff / 1000)

        // 颜色逻辑
        if (totalSeconds < 60) {
          this.timerColor = '#67C23A' // 绿
        } else if (totalSeconds < 90) {
          this.timerColor = '#E6A23C' // 黄
        } else if (totalSeconds < 120) {
          this.timerColor = '#FF7700' // 橙
        } else {
          this.timerColor = '#F56C6C' // 红
        }

        // 超时处理
        if (totalSeconds >= 200 && !this.hasAnswered) {
          this.handleTimeout()
          return
        }

        const mins = Math.floor(totalSeconds / 60)
        const secs = totalSeconds % 60
        this.currentDurationDisplay = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
      }, 1000)
    },

    stopQuestionTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
      if (this.questionStartTime && this.questionElapsedMs == null) {
        this.questionElapsedMs = Date.now() - this.questionStartTime
      }
    },

    async handleTimeout() {
      this.stopQuestionTimer()
      this.$message.warning('答题超时，已自动判错')

      // 标记为已答且错误
      this.hasAnswered = true
      if (Array.isArray(this.answerResults)) {
        this.$set(this.answerResults, this.currentIndex, false)
      }

      // 记录答题结果
      try {
        // 记录为错误，isReview=0
        await this.persistAnswerRecord(false, 0)
        // 记录本次会话统计（不逐题上报 amc-status）
        try { this.recordStatusAnswer(this.currentQuestion.id, false) } catch (_) {}
      } catch (_) {}

      // 更新统计
      this.statCompleted += 1
      this.statWrong += 1
      // 记录已完成题目 ID
      if (this.currentQuestion && this.currentQuestion.id != null) {
        const qid = Number(this.currentQuestion.id)
        if (!this.statFinishedIds.includes(qid)) {
          this.statFinishedIds.push(qid)
        }
      }
      try { await this.reportDailyProgress() } catch (__) {}
      try { await this.maybeReportDailyStatus() } catch (_) {}

      // 弹出错误处理框
      this.showWrongActionDialog = true
    },
    // 获取当前题目的解题用时（秒），返回格式化的字符串
    getQuestionDurationSeconds() {
      if (!this.questionStartTime) return '0'
      const diff = this.questionElapsedMs != null ? this.questionElapsedMs : (Date.now() - this.questionStartTime)
      // 转换为秒，保留2位小数
      return (diff / 1000).toFixed(2)
    },
    // 兼容旧方法名，避免报错
    getQuestionDuration() {
      return this.getQuestionDurationSeconds()
    },
    // 记录并保存当前题目的解题用时
    recordQuestionDuration() {
      if (this.currentIndex >= 0 && Array.isArray(this.questionDurations)) {
        const duration = this.getQuestionDuration()
        this.$set(this.questionDurations, this.currentIndex, duration)
      }
    },
    // 追加写入方案的 finishedId 字段：将 ids 合并去重后回写
    async updateSchemeFinishedIds(userId, repoId, ids) {
      if (!userId || !repoId || !ids || !ids.length) return
      // 使用通用接口获取学习方案
      const resp = await getSchemeByUserIdAndRepoId(userId, repoId)
      const scheme = resp?.data?.data ?? resp?.data ?? resp
      const schemeId = scheme ? (scheme.id ?? scheme.schemeId) : null
      if (!schemeId) return
      // 获取已有的 finishedId
      const existing = scheme?.finishedId || scheme?.finishedIds || scheme?.doneIds || ''
      const existSet = new Set(String(existing || '').split(/[，,\s]+/).filter(x => x && !isNaN(Number(x))).map(x => Number(x)))
      ids.forEach(id => existSet.add(Number(id)))
      const merged = Array.from(existSet).filter(n => !isNaN(n) && n > 0).join(',')
      // 使用通用接口更新 finishedId
      await updateSchemeFinished(schemeId, merged)
    }
  },
  watch: {
    // 监听当前题目变化，更新全局上下文供AI助手使用
    currentQuestion: {
      handler(newVal) {
        if (newVal) {
          const ctx = {
            id: newVal.id,
            content: newVal.content || newVal.contentCn || newVal.contentEn || '',
            answer: newVal.answer,
            analysis: newVal.analysis || newVal.analysisCn || newVal.analysisEn || '',
            knowledge: newVal.knowledge || newVal.knowledgeName || ''
          }
          window.__currentQuestionContext = ctx
          try {
            localStorage.setItem('currentQuestionContext', JSON.stringify(ctx))
          } catch (e) {}
        } else {
          window.__currentQuestionContext = null
          try {
            localStorage.removeItem('currentQuestionContext')
          } catch (e) {}
        }
      },
      immediate: true
    },

    // 作答/超时后立刻停表
    hasAnswered(val) {
      if (val) this.stopQuestionTimer()
    },

    // 切换题号后，确保计时器重新开始（以 currentIndex 为准更稳）
    currentIndex() {
      this.$nextTick(() => {
        try {
          if (!this.hasAnswered) this.startQuestionTimer()
        } catch (_) {}
      })
    }
  },
  computed: {
    // 当前题目标题：如"题目 1""题目 2"
    questionTitle() {
      if (!this.questions || !this.questions.length) return ''
      return `题目 ${this.currentIndex + 1}`
    },
    // 当前题目是否已完成（用于禁用再次提交）
    isCurrentFinished() {
      if (!this.currentQuestion || this.currentQuestion.id == null) return false
      if (!Array.isArray(this.statFinishedIds) || !this.statFinishedIds.length) return false
      const qid = Number(this.currentQuestion.id)
      return this.statFinishedIds.some(id => Number(id) === qid)
    },
    similarCurrent() {
      if (!this.similarList || this.similarList.length === 0) return null
      return this.similarList[this.similarIndex] || null
    }
  },
  beforeDestroy() {
    // 清理计时器
    this.stopQuestionTimer()
    // 退出页面时上报无尽刷题汇总 status
    this.flushEndlessStatusOnExit()
    // 离开页面时清除题目上下文
    window.__currentQuestionContext = null
    try {
      localStorage.removeItem('currentQuestionContext')
    } catch (e) {}
  }
}
</script>

<style scoped>
@import './DailyQuestion.css';

/* 整体容器 */
.daily-question-container {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  padding-bottom: 30px;
}

/* 顶部导航栏 */
.daily-header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 20px 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid rgba(102, 126, 234, 0.2);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.back-btn {
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #667eea;
  padding: 5px;
  border-radius: 50%;
}

.back-btn:hover {
  background: rgba(102, 126, 234, 0.1);
  transform: translateX(-3px);
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 15px;
}

/* 计时器样式 */
.timer-display {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 8px 20px;
  border-radius: 25px;
  font-weight: 700;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  animation: pulse 2s infinite;
}

.timer-display i {
  font-size: 20px;
}

@keyframes pulse {
  0%, 100% { box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4); }
  50% { box-shadow: 0 4px 20px rgba(102, 126, 234, 0.6); }
}

/* 题目选择卡片 */
.plan-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 15px;
  padding: 20px 30px;
  max-width: 1400px;
  margin: 0 auto;
}

.plan-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border: 2px solid #e5e7eb;
  border-radius: 15px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
}

.plan-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.plan-card:hover {
  transform: translateY(-5px) scale(1.02);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.2);
  border-color: #667eea;
}

.plan-card:hover::before {
  transform: scaleX(1);
}

.plan-card.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  transform: translateY(-5px) scale(1.05);
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
}

.plan-card.active .plan-card-title,
.plan-card.active .plan-card-id {
  color: white;
}

.plan-card-title {
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 6px;
  color: #333;
}

.plan-card-id {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

/* 题目卡片 */
.question-card {
  background: white;
  border-radius: 20px;
  padding: 35px;
  margin: 20px auto;
  max-width: 1200px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  animation: fadeInUp 0.5s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
}

.question-title {
  display: flex;
  align-items: center;
  gap: 15px;
}

.question-number {
  font-size: 22px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.question-date {
  color: #999;
  font-size: 14px;
  padding: 5px 12px;
  background: #f5f5f5;
  border-radius: 20px;
}

.question-content {
  font-size: 18px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9ff;
  border-radius: 12px;
  border-left: 4px solid #667eea;
}

/* 题目图片样式 */
.question-images {
  margin: 20px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.question-image {
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.question-image:hover {
  transform: scale(1.02);
}

/* 选项样式 */
.question-options {
  margin-bottom: 30px;
}

.option-item {
  display: block;
  margin-bottom: 15px;
}

.option-item >>> .el-radio {
  width: 100%;
  padding: 18px 20px;
  margin: 0;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.option-item >>> .el-radio:hover {
  border-color: #667eea;
  background: #f8f9ff;
  transform: translateX(5px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.15);
}

.option-item >>> .el-radio.is-checked {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  color: white;
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
}

.option-item >>> .el-radio.is-checked .el-radio__label {
  color: white !important;
}

/* 选项图片样式 */
.option-with-image {
  display: flex;
  align-items: center;
  gap: 10px;
}

.option-key {
  font-weight: 600;
  font-size: 16px;
  min-width: 30px;
}

.option-image {
  max-width: 200px;
  max-height: 150px;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.option-item >>> .el-radio.is-checked .option-key {
  color: white !important;
}

/* 答题输入框 */
.question-answer-input {
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  gap: 15px;
}

.answer-tip {
  color: #999;
  font-size: 14px;
}

/* 上传区域 */
.upload-section {
  margin: 25px 0;
  padding: 20px;
  background: #f8f9ff;
  border-radius: 12px;
  border: 2px dashed #d0d5dd;
}

.upload-title {
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
  font-size: 16px;
}

/* 答题状态 */
.answer-status {
  margin: 25px 0;
  padding: 18px 25px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  animation: slideIn 0.5s ease;
  display: flex;
  align-items: center;
  gap: 12px;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.correct-answer {
  background: linear-gradient(135deg, #d4f4dd 0%, #a8e6cf 100%);
  color: #0a5e2a;
  border: 2px solid #52c41a;
  box-shadow: 0 4px 15px rgba(82, 196, 26, 0.2);
}

.correct-answer i {
  font-size: 24px;
}

.incorrect-answer {
  background: linear-gradient(135deg, #ffe5e5 0%, #ffd1d1 100%);
  color: #c41a1a;
  border: 2px solid #ff4d4f;
  box-shadow: 0 4px 15px rgba(255, 77, 79, 0.2);
}

.incorrect-answer i {
  font-size: 24px;
}

/* 解析区域 */
.analysis-section {
  margin-top: 25px;
  padding: 25px;
  background: linear-gradient(135deg, #fff9e6 0%, #ffe9b3 100%);
  border-radius: 12px;
  border-left: 5px solid #faad14;
  animation: fadeIn 0.6s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.analysis-title {
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 15px;
  color: #d48806;
  display: flex;
  align-items: center;
  gap: 8px;
}

.analysis-content {
  line-height: 1.8;
  color: #333;
  font-size: 16px;
}

/* 操作按钮 */
.action-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-top: 35px;
  padding-top: 25px;
  border-top: 2px solid #f0f0f0;
}

.action-buttons >>> .el-button {
  padding: 14px 35px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 25px;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.action-buttons >>> .el-button--primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.action-buttons >>> .el-button--primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.action-buttons >>> .el-button--default {
  background: white;
  color: #667eea;
  border: 2px solid #667eea;
}

.action-buttons >>> .el-button--default:hover {
  background: #f8f9ff;
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.2);
}

/* AI 判题结果样式 */
.ai-analysis-content {
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 10px;
}

.ai-analysis-content .analysis-section {
  margin-bottom: 20px;
  padding: 20px;
  background: #f8f9ff;
  border-radius: 10px;
}

.ai-analysis-content .section-title {
  margin: 0 0 12px;
  font-weight: 700;
  font-size: 18px;
  color: #333;
}

.ai-analysis-content .answer-comparison {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.ai-analysis-content .answer-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: white;
  border-radius: 8px;
}

.ai-analysis-content .answer-label {
  color: #666;
  font-weight: 600;
}

.ai-analysis-content .answer-value.user-answer {
  color: #f56c6c;
  font-weight: 600;
}

.ai-analysis-content .answer-value.correct-answer {
  color: #67c23a;
  font-weight: 600;
}

.ai-analysis-content .ai-result .result-item {
  margin: 12px 0;
  padding: 12px;
  background: white;
  border-radius: 8px;
}

.ai-analysis-content .ai-result .result-label {
  color: #666;
  margin-right: 10px;
  font-weight: 600;
}

.ai-analysis-content .ai-result .result-content {
  white-space: pre-wrap;
  line-height: 1.8;
  color: #303133;
}

.ai-analysis-content .ai-result .result-content.suggestion {
  background: linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%);
  border-left: 4px solid #409EFF;
  padding: 15px;
  border-radius: 8px;
}

/* 加载和错误状态 */
.loading-container,
.error-container,
.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.error-content,
.empty-content {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.error-icon,
.empty-icon {
  font-size: 64px;
  color: #ff4d4f;
  margin-bottom: 20px;
}

.empty-icon {
  color: #d9d9d9;
}

/* 滚动条美化 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}
</style>
